import { useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { MiModal } from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_LABELS,
  BUTTON_VARIANTS,
  CONFIG_PAGES,
  ENTITY_NAMES,
  PERMISSIONS,
} from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import {
  IInspectionTemplatePayload,
  IVerticalTabProps,
} from "@miplatform/types";
import { getUpdatedFields } from "@miplatform/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { mdiAlertCircleOutline } from "@mdi/js";
import { VerticalTabsLayout } from "@mibuildweb/components";
import { useInspectionTemplate, usePermissions } from "@mibuildweb/hooks";
import { Typography, useTheme } from "@mui/material";
import { InspectionTemplateAttachOrderTemplatesTab } from "./tabs/InspectionTemplateAttachOrderTemplatesTab";
import { InspectionTemplateDetailsTab } from "./tabs/InspectionTemplateDetailsTab";
import { InspectionTemplateRescheduleReasonsTab } from "./tabs/InspectionTemplateRescheduleReasonsTab";
import { CONFIG_PATHS } from "../../../../routes/config/constants";
import { ConfigPageLayout } from "../../ConfigPageLayout";
import { configInspectionTemplateSchema } from "../schemas/ConfigInspectionTemplate.schema";

export const ConfigInspectionTemplateDetails = () => {
  const { params, goBack, navigate } = useRouter();
  const theme = useTheme();
  const { id: inspectionTemplateId } = params;
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  const {
    inspectionTemplateDetails,
    getInspectionTemplateDetails,
    updateInspectionTemplate,
    createInspectionTemplate,
    loading,
  } = useInspectionTemplate();

  const { hasSomePermissions } = usePermissions();

  const canUpdateInspectionTemplate = hasSomePermissions([
    PERMISSIONS.INSPECTION_TEMPLATE.UPDATE,
    PERMISSIONS.SYSTEM_ADMINISTRATION.ADMINISTRATION,
  ]);

  const isReadOnly = !canUpdateInspectionTemplate;

  const form = useForm<IInspectionTemplatePayload>({
    mode: "onChange",
    defaultValues: INITIAL_INSPECTION_TEMPLATE_DATA,
    resolver: zodResolver(configInspectionTemplateSchema),
  });

  const {
    control,
    formState: { isValid, isDirty, dirtyFields },
    getValues,
    reset,
  } = form;

  const isActive = inspectionTemplateDetails?.isActive;

  const watchName = useWatch({
    control,
    name: "name",
  });

  const rescheduledReasonIds = useWatch({
    control,
    name: "rescheduledReasonIds",
  });

  const attachedOrderTemplateIds = useWatch({
    control,
    name: "orderTemplateIds",
  });

  const areOrderTemplatesComplete =
    attachedOrderTemplateIds && attachedOrderTemplateIds?.length > 0;

  const areRescheduleReasonsComplete =
    rescheduledReasonIds && rescheduledReasonIds?.length > 0;

  const handleSubmitInspectionTemplate = (isActive: boolean) => {
    const data = { ...getValues(), isActive };
    if (inspectionTemplateId) {
      const updatedFields: IInspectionTemplatePayload = getUpdatedFields(
        dirtyFields,
        data
      );
      updatedFields.isActive = data.isActive;

      updateInspectionTemplate(inspectionTemplateId, updatedFields, {
        name: data.name,
        onSuccess: reset,
      });
    } else {
      createInspectionTemplate(data, {
        onSuccess: () =>
          navigate(CONFIG_PATHS[CONFIG_PAGES.INSPECTION_TEMPLATES]),
      });
    }
    setIsConfirmModalOpen(false);
  };

  const renderPageTitle = () => {
    const action = inspectionTemplateId ? "Edit" : "Create";
    const inspectionTemplateLabel = watchName
      ? `${watchName}`
      : `New ${ENTITY_NAMES.INSPECTION_TEMPLATE}`;
    return (
      <Typography variant="HD4">
        {action} {ENTITY_NAMES.INSPECTION_TEMPLATE}:{" "}
        <Typography variant="HD4" color="primary" component="span">
          {inspectionTemplateLabel}
        </Typography>
      </Typography>
    );
  };

  const tabs = useMemo<IVerticalTabProps<IInspectionTemplatePayload>[]>(
    () => [
      {
        label: `${ENTITY_NAMES.INSPECTION_TEMPLATE} Details`,
        value: "details",
        extraTabProps: {
          isReadOnly,
        },
        controlledFormViewComponent: InspectionTemplateDetailsTab,
        isComplete: isValid,
        hasErrors: !isValid,
      },
      {
        label: "Attach Order Templates",
        value: "attachOrderTemplates",
        extraTabProps: {
          isReadOnly,
        },
        controlledFormViewComponent: InspectionTemplateAttachOrderTemplatesTab,
        isComplete: areOrderTemplatesComplete,
      },
      {
        label: "Reschedule Reasons",
        value: "rescheduleReasons",
        extraTabProps: {
          isReadOnly,
        },
        controlledFormViewComponent: InspectionTemplateRescheduleReasonsTab,
        isComplete: areRescheduleReasonsComplete,
      },
    ],
    [
      isValid,
      areOrderTemplatesComplete,
      areRescheduleReasonsComplete,
      isReadOnly,
    ]
  );

  const actions = {
    cancel: {
      label: BUTTON_LABELS.CANCEL,
      onClick: () => {
        reset();
        goBack();
      },
    },
    submit: {
      label: `Save ${ENTITY_NAMES.INSPECTION_TEMPLATE}`,
      onClick: () => {
        setIsConfirmModalOpen(true);
      },
      disabled: !isValid || !isDirty || isReadOnly,
    },
  };

  useEffect(() => {
    if (inspectionTemplateId) {
      getInspectionTemplateDetails(inspectionTemplateId, {
        onSuccess: reset,
      });
    }
  }, [inspectionTemplateId]);

  useEffect(() => {
    if (inspectionTemplateId) {
      getInspectionTemplateDetails(inspectionTemplateId);
    }
  }, [inspectionTemplateId]);

  return (
    <>
      <ConfigPageLayout
        renderTitle={renderPageTitle}
        bottomActions={actions}
        loading={loading}
      >
        <VerticalTabsLayout form={form} tabs={tabs} transparentBackground />
      </ConfigPageLayout>
      <MiModal
        title={`You Are Saving Your ${ENTITY_NAMES.INSPECTION_TEMPLATE}`}
        description={
          <>
            You are saving{" "}
            <span
              style={{
                color: theme.palette.primary.main,
                fontWeight: theme.fontWeight.semiBold,
              }}
            >
              {watchName}
            </span>
            . Would you like to activate this{" "}
            {ENTITY_NAMES.INSPECTION_TEMPLATE.toLowerCase()} to be used in your
            org?
          </>
        }
        titleIcon={mdiAlertCircleOutline}
        leftIconColor={theme.palette.primary.main}
        open={isConfirmModalOpen}
        closeModal={() => setIsConfirmModalOpen(false)}
        actions={[
          {
            name: `Save ${ENTITY_NAMES.INSPECTION} As Draft`,
            color: BUTTON_COLORS.ORANGE,
            variant: BUTTON_VARIANTS.CONTAINED,
            onClick: () => handleSubmitInspectionTemplate(false),
          },
          {
            name: isActive
              ? `Save ${ENTITY_NAMES.INSPECTION}`
              : `Activate ${ENTITY_NAMES.INSPECTION}`,
            color: BUTTON_COLORS.SUCCESS,
            variant: BUTTON_VARIANTS.CONTAINED,
            onClick: () => handleSubmitInspectionTemplate(true),
          },
        ]}
      />
    </>
  );
};

const INITIAL_INSPECTION_TEMPLATE_DATA: IInspectionTemplatePayload = {
  name: "",
  description: "",
  abbreviation: "",
  color: "",
  allowAiRedTag: false,
  orderTemplateIds: [],
  rescheduledReasonIds: [],
};
